import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { catchError, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { DialogService } from '../../../domain/services/dialog/dialog.service';

export interface AdminDistribution {
  id: number;
  distributionPeriod: string;
  amount: number;
  status: string;
  dateCreated: string;
}

const MOCK_DATA: AdminDistribution[] = [
  { id: 1, distributionPeriod: 'January 2025', amount: 124500.00, status: 'Completed', dateCreated: '2025-02-01' },
  { id: 2, distributionPeriod: 'February 2025', amount: 98750.50, status: 'Completed', dateCreated: '2025-03-01' },
  { id: 3, distributionPeriod: 'March 2025', amount: 136200.75, status: 'Pending', dateCreated: '2025-04-01' },
  { id: 4, distributionPeriod: 'April 2025', amount: 0.00, status: 'Draft', dateCreated: '2025-05-01' },
];

@Component({
  selector: 'app-distribution-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatDatepickerModule, MatButtonModule, MatIconModule, MatDialogModule],
  template: `
    <div style="padding: 8px;">
      <h2 mat-dialog-title style="display:flex;align-items:center;gap:12px;font-size:20px;font-weight:600;color:#1e293b;">
        <mat-icon style="color:#2563eb;">pie_chart</mat-icon>
        {{ data.dist ? 'Edit Distribution' : 'Add Distribution' }}
      </h2>
      <mat-dialog-content>
        <form [formGroup]="form" style="display:flex;flex-direction:column;gap:16px;min-width:420px;">
          <div class="field-group">
            <label style="font-size:13px;font-weight:500;color:#475569;margin-bottom:6px;display:block;">Period <span style="color:#dc2626;">*</span></label>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" style="width:100%;">
              <input matInput formControlName="distributionPeriod" placeholder="e.g. January 2025">
              <mat-error>Period is required</mat-error>
            </mat-form-field>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
            <div class="field-group">
              <label style="font-size:13px;font-weight:500;color:#475569;margin-bottom:6px;display:block;">Amount ($)</label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic" style="width:100%;">
                <input matInput type="number" formControlName="amount" placeholder="0.00">
              </mat-form-field>
            </div>
            <div class="field-group">
              <label style="font-size:13px;font-weight:500;color:#475569;margin-bottom:6px;display:block;">Status <span style="color:#dc2626;">*</span></label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic" style="width:100%;">
                <mat-select formControlName="status">
                  <mat-option value="Draft">Draft</mat-option>
                  <mat-option value="Pending">Pending</mat-option>
                  <mat-option value="Completed">Completed</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>
        </form>
      </mat-dialog-content>
      <mat-dialog-actions align="end" style="padding:16px 0 0;gap:12px;">
        <button mat-stroked-button (click)="dialogRef.close()" style="color:#2563eb;"><mat-icon>arrow_back</mat-icon> GO BACK</button>
        <button mat-flat-button color="primary" [disabled]="form.invalid" (click)="save()"><mat-icon>save</mat-icon> SAVE</button>
      </mat-dialog-actions>
    </div>
  `
})
export class DistributionDialogComponent {
  form: FormGroup;
  constructor(
    public dialogRef: MatDialogRef<DistributionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { dist?: AdminDistribution }
  ) {
    this.form = new FormGroup({
      distributionPeriod: new FormControl(data.dist?.distributionPeriod ?? null, [Validators.required]),
      amount: new FormControl(data.dist?.amount ?? null),
      status: new FormControl(data.dist?.status ?? 'Draft', [Validators.required]),
    });
  }
  save() { this.dialogRef.close(this.form.value); }
}

@Component({
  selector: 'app-admin-distributions',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatPaginatorModule,
    MatSortModule,
  ],
  templateUrl: './admin-distributions.component.html',
  styleUrl: './admin-distributions.component.css',
})
export class AdminDistributionsComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<AdminDistribution>;

  displayedColumns = ['distributionPeriod', 'amount', 'status', 'dateCreated', 'actions'];
  dataSource = new MatTableDataSource<AdminDistribution>([]);

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    private readonly http: HttpClient,
    private readonly dialog: MatDialog,
    private readonly dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'account_balance', url: '/admin/distributions', label: 'Administrative' },
      { icon: 'pie_chart', url: '/admin/distributions', label: 'Distributions' },
    ]);

    this.http.get<AdminDistribution[]>(`${environment.apiUrl}/api/distributions`)
      .pipe(catchError(() => of(MOCK_DATA)))
      .subscribe((data) => { this.dataSource.data = data; });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadData(): void {
    this.http.get<AdminDistribution[]>(`${environment.apiUrl}/api/distributions`)
      .pipe(catchError(() => of(MOCK_DATA)))
      .subscribe((data) => { this.dataSource.data = data; });
  }

  addDistribution(): void {
    const dialogRef = this.dialog.open(DistributionDialogComponent, {
      width: '520px', data: {}
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Add locally since backend is mock
        const newDist = { ...result, id: Date.now(), dateCreated: new Date().toISOString().split('T')[0] };
        this.dataSource.data = [newDist, ...this.dataSource.data];
      }
    });
  }

  editDistribution(dist: AdminDistribution): void {
    const dialogRef = this.dialog.open(DistributionDialogComponent, {
      width: '520px', data: { dist }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dataSource.data = this.dataSource.data.map(d =>
          d.id === dist.id ? { ...d, ...result } : d
        );
      }
    });
  }

  deleteDistribution(dist: AdminDistribution): void {
    this.dialogService.openDeletedDialog(
      'Delete Distribution',
      `Are you sure you want to delete "${dist.distributionPeriod}"? This action cannot be undone.`,
      (confirmed: boolean) => {
        if (confirmed) {
          this.dataSource.data = this.dataSource.data.filter(d => d.id !== dist.id);
        }
      }
    );
  }
}
